import React, { Component, Fragment } from 'react';

import Group from '@lightspeed/cirrus-group';
import Modal, { ModalHeader, ModalBody, ModalFooter } from '@lightspeed/cirrus-modal';
import Select from '@lightspeed/cirrus-select';
import { Button } from '@lightspeed/flame/Button';
import { Divider } from '@lightspeed/flame/Divider';
import { IconChevronLeft } from '@lightspeed/flame/Icon/ChevronLeft';
import { IconChevronRight } from '@lightspeed/flame/Icon/ChevronRight';
import { IconInfo } from '@lightspeed/flame/Icon/Info';
import PropTypes from 'prop-types';
import NumericInput from 'react-numeric-input';

import { LabelContent } from 'label/utils/types';
import { getOptions } from 'pages/views/devices/utils/options';
import { safeGetItem } from 'pages/views/devices/utils/storage';
import Format from 'root/apps/app/src/data/Format';
import { handlePrintedCheckboxes } from 'root/merchantos/label-print-helper';

import LabelPrintModal from '../LabelPrintModal';
import LabelPrintTracking from '../LabelPrintTracking';
import { getLabelData } from '../utils/printer';

import { DeprecationNotice } from './DeprecationNotice';

const labelSizes = {
  normal: 'Normal - 2.25" x 1.25"',
  small: 'Small - 1.25" x 1.00"',
  alt: 'Alt - 2.00" x 1.00"',
  jewelry: 'Jewelry - 2.20" x 0.5"',
};

const labelMargins = {
  min: -20,
  max: 20,
};

/**
 * Class Print displays the print settings (printer, label size, label amount, etc)
 */
class Print extends Component {
  /**
   * Construct the Print Modal
   * @param {Object} props - Component properties
   */
  constructor(props) {
    super(props);

    const labelSize = safeGetItem('LABEL_SIZE', 'normal');
    // eslint-disable-next-line radix
    const labelMargin =
      parseInt(localStorage.getItem(`LABEL_MARGIN_${labelSize.toUpperCase()}`), 10) || 0;

    this.state = {
      showRecalibrateNote: false,
      totalProducts: 0,
      labelQuantity: 0,
      labelSize,
      labelMargin,
      isQueue: null,
      labelData: null,
      labelContent: null,
    };

    this.isOpenedPrintTracked = false;

    this.labelInputRef = React.createRef();

    this.close = this.close.bind(this);
    this.selectPrinter = this.selectPrinter.bind(this);
    this.savePrintLabelPreference = this.savePrintLabelPreference.bind(this);
    this.selectLabelSize = this.selectLabelSize.bind(this);
    this.setLabelQuantity = this.setLabelQuantity.bind(this);
    this.handleLabelMarginChange = this.handleLabelMarginChange.bind(this);
    this.handleLabelMarginBlur = this.handleLabelMarginBlur.bind(this);
    this.handleLabelMarginFocus = this.handleLabelMarginFocus.bind(this);
    this.getLabelData();
    this.bindEnterKeyToPrint();
  }

  /**
   * React's Unmount component listener
   */
  componentWillUnmount() {
    $(document).off('.labelPrint');
  }

  async setLabelContent() {
    const options = getOptions();
    const { accountId, shopId } = options;

    try {
      const res = await fetch(`/API/Account/${accountId}/Shop/${shopId}.json`)
        .then(response => {
          return response.json();
        })
        .catch(err => {
          // eslint-disable-next-line no-console
          console.error('Error retrieving shop data', err);
        });

      const {
        Shop: { labelMsrp },
      } = res;

      const labelMsrpAccountLevelSetting = Format.parseBoolean(labelMsrp, false);

      if (labelMsrpAccountLevelSetting) {
        this.setState({
          labelContent: LabelContent.MSRP,
        });
        return;
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error retrieving shop data', err);

      this.setState({
        labelContent: LabelContent.NONE,
      });
    }

    this.setState({
      labelContent: LabelContent.NONE,
    });
  }

  /**
   * Get the label data to update the modal
   */
  async getLabelData() {
    await this.setLabelContent();

    getLabelData(this.props.labelApiUrl).then(
      data => {
        // Perhaps the step changed before receiving the label data
        if (this.props.getStep() !== LabelPrintModal.steps.PRINT) return;
        if (!data || typeof data.Label !== 'object') {
          this.props.setStep(LabelPrintModal.steps.ERROR, { error: 'EMPTY_API_RESPONSE' });
          return;
        }

        let isQueue = false;

        let totalProducts = 1;

        let labelQuantity = 0;

        // Print Queue of label
        if (Array.isArray(data.Label)) {
          isQueue = true;

          totalProducts = data.Label.length;
          // eslint-disable-next-line no-plusplus
          for (let i = 0, ii = data.Label.length; i < ii; ++i) {
            // eslint-disable-next-line radix
            labelQuantity += parseInt(data.Label[i].copies);
          }
        } else {
          // Print individual product
          // eslint-disable-next-line radix
          labelQuantity = this.state.labelQuantity || parseInt(data.Label.copies) || 1;
        }

        this.setState({
          isQueue,
          labelData: data.Label,
          totalProducts,
          labelQuantity,
        });
      },
      () => {
        // Label API Url is unreachable
        this.props.setStep(LabelPrintModal.steps.ERROR, { error: 'GENERIC' });
      }
    );
  }

  /**
   * If the input changes, set the label quantity
   * @param {String} eventKey - Value of the "eventKey" attribute from the selected element if any
   * @param {Object} event - The selected item
   */
  setLabelQuantity(eventKey, event) {
    this.setState({
      // eslint-disable-next-line radix
      labelQuantity: parseInt(event, 10),
    });
  }

  /**
   * Select the label size from the dropdown
   * @param {Object} event - The selected item
   */
  selectLabelSize(event) {
    const { value: labelSize } = event.currentTarget;
    if (labelSize === this.state.labelSize) return;

    // eslint-disable-next-line radix
    const labelMargin =
      parseInt(localStorage.getItem(`LABEL_MARGIN_${labelSize.toUpperCase()}`), 10) || 0;
    LabelPrintTracking.track(LabelPrintTracking.events.CHANGED_LABEL, { labelSize });

    this.setState({
      showRecalibrateNote: true,
      labelSize,
      labelMargin,
    });
  }

  /**
   * Select the printer from the dropdown
   * @param {Object} event - The selected item
   */
  selectPrinter(event) {
    const { value } = event.currentTarget;
    // eslint-disable-next-line no-shadow
    const printer = this.props.printers.find(printer => printer.uid === value);

    // Make sure we are not selecting the same printer
    if (!printer || printer.uid === this.props.printer.uid) return;

    // Allow to send ZPL commands to the selected printer
    localStorage.setItem('LABEL_PRINTER', printer.uid.trim());

    LabelPrintTracking.track(LabelPrintTracking.events.CHANGED_PRINTER);

    this.props.setPrinter(printer);
  }

  /**
   * Save the last printed label size in localStorage and select it by default
   * the next time the user wants to print a label
   */
  savePrintLabelPreference() {
    localStorage.setItem('LABEL_PRINTER', this.props.printer.uid.trim());
    localStorage.setItem('LABEL_SIZE', this.state.labelSize);
  }

  /**
   * Bind "ENTER" to send click the print button
   */
  bindEnterKeyToPrint() {
    $(document)
      .off('.labelPrint')
      .on('keypress.labelPrint', e => {
        if (e.which === 13) {
          $('.js-label-print').trigger('click');
        }
      });
  }

  /**
   * Implement additional functionality to the onHide/close parent function
   */
  close() {
    LabelPrintTracking.track(LabelPrintTracking.events.CLOSED_WITHOUT_PRINTING);
    this.props.onHide();
  }

  /**
   * Set label margin inside the state from a change event,
   * also write the value inside localStorage so the input gets populated the next time
   * @param {Number} labelMargin - Event representing the changed input
   */
  handleLabelMarginChange(labelMargin, labelMarginText) {
    if (
      typeof labelMargin === 'number' &&
      labelMargin >= labelMargins.min &&
      labelMargin <= labelMargins.max
    ) {
      localStorage.setItem(`LABEL_MARGIN_${this.state.labelSize.toUpperCase()}`, labelMargin);
      LabelPrintTracking.track(LabelPrintTracking.events.ADJUSTED_MARGIN);
    }

    this.setState({
      labelMargin: typeof labelMargin === 'number' ? labelMargin : labelMarginText,
    });
  }

  /**
   * Set label margin inside the state from a blur event
   * If the input value is not matched form the accepted pattern, it resets to 0
   * @param {String} event - Event representing the changed input
   */
  handleLabelMarginBlur(event) {
    const labelMargin = event.target.value;
    if (!/^(?:-?[\d]+)$/.test(labelMargin)) {
      this.setState({
        labelMargin: 0,
      });
    }
  }

  /**
   * Selects all in label margin input on focus
   * @param {Object} event - Event representing the focused NumericInput
   */
  handleLabelMarginFocus() {
    this.labelInputRef.current.refs.input.select();
  }

  handleHelpOnClick() {
    LabelPrintTracking.track(LabelPrintTracking.events.VISITED_HELP, { step: 'Print' });
  }

  /**
   * Render the Print modal
   * @returns {Object} DOM Element
   */
  render() {
    const {
      labelSize,
      labelData,
      totalProducts,
      labelQuantity,
      labelMargin,
      isQueue,
      labelContent,
    } = this.state;
    const { printer, printers } = this.props;

    let isPrintEnabled = false;
    if (Object.keys(printer).length && labelData && labelQuantity >= 1) {
      isPrintEnabled = true;
      if (!this.isOpenedPrintTracked) {
        this.isOpenedPrintTracked = true;
        LabelPrintTracking.track(LabelPrintTracking.events.OPENED_PRINT, {
          type: isQueue ? 'queue' : 'single',
        });
      }
    }

    return (
      <Modal
        className={`label-print-modal ${this.props.currentStep.toLowerCase()} cirrus responsive`}
        isOpen={this.props.show}
        onRequestClose={this.props.onHide}
      >
        <ModalHeader onCloseClicked={this.props.onHide}>
          Print Label{' '}
          {labelData && (
            <Fragment>
              {isQueue ? ' Queue' : ''} -{' '}
              {isQueue ? (
                <Fragment>
                  <var>{totalProducts}</var> Items,
                  <var>{labelQuantity}</var> Labels
                </Fragment>
              ) : (
                <var>{labelData.Item.description}</var>
              )}
            </Fragment>
          )}
        </ModalHeader>
        <ModalBody>
          <div className="cr-mb-2 cr-mt-4">
            <DeprecationNotice callback={this.close} />
          </div>
          <div
            data-test="zbp-label-print-modal"
            className="cr-flex cr-width-full cr-items-center cr-justify-between cr-pv-4"
          >
            <p className="cr-mv-0">Printer</p>
            <Select
              disabled={!Object.keys(printer).length}
              className="cr-width-6-12 notranslate"
              onChange={this.selectPrinter}
              value={this.props.printer.uid}
            >
              {Object.keys(printer).length ? (
                // eslint-disable-next-line no-shadow
                printers.map((printer, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <option key={index} className="notranslate" value={printer.uid}>
                    {printer.connection}:{printer.uid}
                  </option>
                ))
              ) : (
                <option>Detecting printers...</option>
              )}
            </Select>
          </div>
          <div style={{ margin: '0 -1.5rem' }}>
            <Divider />
          </div>
          <div className="cr-pv-4">
            <div className="cr-flex cr-width-full cr-items-center cr-justify-between">
              <p className="cr-mv-0">Label Size</p>
              <Select
                className="cr-width-6-12 notranslate"
                onChange={this.selectLabelSize}
                value={this.state.labelSize}
                data-test="label-size"
              >
                {Object.keys(labelSizes).map(
                  // eslint-disable-next-line no-shadow
                  (labelSize, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <option key={index} value={labelSize}>
                      {labelSizes[labelSize]}
                    </option>
                  )
                )}
              </Select>
            </div>
            {this.state.showRecalibrateNote && (
              <Group className="cr-justify-end cr-text-s cr-mt-2">
                <IconInfo color="blue" />
                <p className="cr-gray-300 cr-m-0">
                  Recalibrate the printer after changing label sizes.
                </p>
                <a
                  href={`${LabelPrintModal.links.documentation}#calibrate`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  See how
                </a>
              </Group>
            )}
          </div>
          <div style={{ margin: '0 -1.5rem' }}>
            <Divider />
          </div>
          <div
            className="cr-flex cr-width-full cr-items-center cr-justify-between cr-pv-4"
            style={{ height: '200px' }}
          >
            <div className="cr-self-start">
              <p className={`cr-mv-0 ${labelData ? '' : 'invisible'}`}>
                <span className="notranslate numeric-input-container">
                  {this.state.isQueue === false ? (
                    <NumericInput
                      // eslint-disable-next-line react/no-string-refs
                      ref="labelQuantity"
                      min={1}
                      max={65535}
                      onChange={this.setLabelQuantity}
                      value={this.state.labelQuantity}
                      size={4}
                      autoFocus
                      onFocus={e => {
                        e.target.select();
                      }}
                      style={{
                        input: {
                          marginRight: 3,
                          paddingRight: 4,
                          textAlign: 'right',
                        },
                        btnUp: {
                          display: 'none',
                        },
                        btnDown: {
                          display: 'none',
                        },
                      }}
                    />
                  ) : (
                    this.state.labelQuantity
                  )}
                  x
                </span>{' '}
                <span className="cr-text-transform-capitalize">{labelSize}</span> Label
              </p>
              <p className="label-size-note cr-gray-300 cr-text-xs">
                Make sure the printer is loaded with enough{' '}
                <span className="cr-text-transform-capitalize">{labelSize}</span> labels.
              </p>
            </div>
            <div className="cr-text-center cr-width-6-12">
              <img
                className={`label-size-${labelSize}`}
                src={`https://d2o5po5b88zacb.cloudfront.net/dist/assets/lshash-9919532/images/label/label-size-${labelSize}.svg`}
                alt={`${labelSize} labels`}
              />
              <div className="cr-flex cr-width-full cr-justify-between">
                <div className="cr-text-s">Adjust Margin</div>
                <div className="cr-position-relative" style={{ left: '-20px' }}>
                  <div className="cr-position-absolute" style={{ left: '-18px' }}>
                    <IconChevronLeft
                      color={this.state.labelMargin <= labelMargins.min ? 'gray-100' : 'night'}
                    />
                  </div>
                  <div className="cr-position-absolute" style={{ right: '-18px' }}>
                    <IconChevronRight
                      color={this.state.labelMargin >= labelMargins.max ? 'gray-100' : 'night'}
                    />
                  </div>
                  <NumericInput
                    ref={this.labelInputRef}
                    min={labelMargins.min}
                    max={labelMargins.max}
                    step={1}
                    onChange={this.handleLabelMarginChange}
                    onBlur={this.handleLabelMarginBlur}
                    value={this.state.labelMargin}
                    size={3}
                    maxLength={3}
                    onFocus={this.handleLabelMarginFocus}
                    style={{
                      input: {
                        margin: 0,
                        paddingRight: '3px',
                        textAlign: 'center',
                      },
                      btnUp: {
                        top: 0,
                        right: '-24px',
                        width: '22px',
                        background: 'transparent',
                        boxShadow: 'none',
                        border: 'none',
                        height: '30px',
                        borderRadius: '3px',
                        cursor:
                          this.state.labelMargin >= labelMargins.max ? 'not-allowed' : 'pointer',
                      },
                      arrowUp: {
                        borderColor: 'transparent',
                      },
                      btnDown: {
                        top: 0,
                        right: '40px',
                        width: '22px',
                        background: 'transparent',
                        boxShadow: 'none',
                        border: 'none',
                        height: '30px',
                        borderRadius: '3px',
                        cursor:
                          this.state.labelMargin <= labelMargins.min ? 'not-allowed' : 'pointer',
                      },
                      arrowDown: {
                        borderColor: 'transparent',
                      },
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <div className="cr-flex cr-justify-between">
            <a href={LabelPrintModal.links.documentation} target="_blank" rel="noopener noreferrer">
              <Button variant="secondary" onClick={this.handleHelpOnClick}>
                Help
              </Button>
            </a>

            <Group className="cr-justify-end">
              <Button onClick={this.close}>Cancel</Button>
              <Button
                variant="primary"
                fill
                disabled={!isPrintEnabled}
                onClick={() => {
                  this.savePrintLabelPreference();
                  this.props.setStep(LabelPrintModal.steps.PRINTING, {
                    printer,
                    labelSize,
                    labelData,
                    labelContent,
                    totalProducts,
                    labelQuantity,
                    labelMargin,
                    isQueue,
                  });
                  // eslint-disable-next-line eqeqeq
                  if (!isQueue && labelData.copies != labelQuantity) {
                    LabelPrintTracking.track(LabelPrintTracking.events.UPDATED_COPIES);
                  }
                  const labelDataArg = Array.isArray(labelData) ? labelData : [{ ...labelData }];
                  handlePrintedCheckboxes(labelDataArg);
                }}
                className="js-label-print"
              >
                Print [↵]
              </Button>
            </Group>
          </div>
        </ModalFooter>
      </Modal>
    );
  }
}

Print.propTypes = {
  labelApiUrl: PropTypes.string.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  printers: PropTypes.array.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  printer: PropTypes.object.isRequired,
  setStep: PropTypes.func.isRequired,
  currentStep: PropTypes.string.isRequired,
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
};

export default Print;
